import React, { useState, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
    Controls,
    Background,
    applyNodeChanges,
    applyEdgeChanges,
    addEdge,
    Node,
    Edge,
    Connection,
    NodeChange,
    EdgeChange
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Button, Form } from 'react-bootstrap';
import { FaSave, FaArrowLeft, FaBolt, FaQuestionCircle, FaCheckCircle } from 'react-icons/fa';
import { Flow, FlowStep, FlowAction, questionToVariableName } from './types';
import { TriggerNode, StepNode, ActionNode } from './FlowNodes';
import { Icon } from '../../../../../components/common/Icon';

const DRAG_TYPE = 'application/reactflow';

interface FlowDiagramEditorProps {
    initialFlow?: Flow | null;
    onSave: (flow: Flow) => void;
    onCancel: () => void;
}

const defaultFlowTemplate: Flow = {
    id: '',
    name: '',
    trigger_keywords: [],
    is_active: true,
    steps: [{ id: '', question: '', variable: '', type: 'text' }],
    actions: [],
    completion_message_success: 'Thank you! We have received your information.',
    completion_message_failure: 'Something went wrong. Please try again later.'
};

function flowToNodes(f: Flow): Node[] {
    const nodes: Node[] = [];
    let yPos = 50;
    const xPos = 280;

    nodes.push({
        id: 'trigger',
        type: 'triggerNode',
        position: { x: xPos, y: yPos },
        data: { keywords: f.trigger_keywords || [] }
    });

    (f.steps || []).forEach((step) => {
        yPos += 140;
        nodes.push({
            id: step.id,
            type: 'stepNode',
            position: { x: xPos, y: yPos },
            data: { question: step.question || '', variable: step.variable || '' }
        });
    });

    yPos += 140;
    nodes.push({
        id: 'action',
        type: 'actionNode',
        position: { x: xPos, y: yPos },
        data: {
            actions: f.actions || [],
            completion_message_success: f.completion_message_success ?? f.completion_message ?? '',
            completion_message_failure: f.completion_message_failure ?? ''
        }
    });

    return nodes;
}

function flowToEdges(f: Flow): Edge[] {
    const edges: Edge[] = [];
    const steps = f.steps || [];
    const triggerId = 'trigger';
    if (steps.length > 0) {
        edges.push({ id: 'e-trigger-step0', source: triggerId, target: steps[0].id });
        for (let i = 0; i < steps.length - 1; i++) {
            edges.push({ id: `e-step${i}-step${i + 1}`, source: steps[i].id, target: steps[i + 1].id });
        }
        edges.push({ id: 'e-stepLast-action', source: steps[steps.length - 1].id, target: 'action' });
    } else {
        edges.push({ id: 'e-trigger-action', source: triggerId, target: 'action' });
    }
    return edges;
}

const FlowDiagramEditor: React.FC<FlowDiagramEditorProps> = ({ initialFlow, onSave, onCancel }) => {
    const reactFlowRef = useRef<any>(null);
    const flowData = initialFlow
        ? { ...initialFlow }
        : {
            ...defaultFlowTemplate,
            id: Date.now().toString(),
            steps: [{ id: 'step-' + Date.now(), question: '', variable: '', type: 'text' as const }]
        };
    const [flowName, setFlowName] = useState(flowData.name || '');
    const [nodes, setNodes] = useState<Node[]>(() => flowToNodes(flowData));
    const [edges, setEdges] = useState<Edge[]>(() => flowToEdges(flowData));

    const updateNodeData = useCallback((nodeId: string, newData: any) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId ? { ...node, data: { ...node.data, ...newData } } : node
            )
        );
    }, []);

    const nodeTypes = useMemo(() => ({
        triggerNode: TriggerNode,
        stepNode: StepNode,
        actionNode: ActionNode
    }), []);

    const nodesWithHandlers = useMemo(() => nodes.map(node => {
        if (node.type === 'triggerNode') {
            return { ...node, data: { ...node.data, onChange: (kws: string[]) => updateNodeData(node.id, { keywords: kws }) } };
        }
        if (node.type === 'stepNode') {
            return {
                ...node,
                data: {
                    ...node.data,
                    onChange: (field: string, val: string) => {
                        if (field === 'question') {
                            updateNodeData(node.id, { question: val, variable: questionToVariableName(val) });
                        } else {
                            updateNodeData(node.id, { [field]: val });
                        }
                    }
                }
            };
        }
        if (node.type === 'actionNode') {
            return {
                ...node,
                data: {
                    ...node.data,
                    onChange: (field: string, val: string) => updateNodeData(node.id, { [field]: val }),
                    onAddAction: () => {
                        const newAction: FlowAction = { id: Date.now().toString(), type: 'email', recipient_email: '', subject: 'New Submission' };
                        const currentActions = node.data.actions || [];
                        updateNodeData(node.id, { actions: [...currentActions, newAction] });
                    },
                    onRemoveAction: (idx: number) => {
                        const currentActions = node.data.actions || [];
                        updateNodeData(node.id, { actions: currentActions.filter((_: any, i: number) => i !== idx) });
                    },
                    onUpdateAction: (idx: number, field: string, val: string) => {
                        const currentActions = [...(node.data.actions || [])];
                        currentActions[idx] = { ...currentActions[idx], [field]: val };
                        updateNodeData(node.id, { actions: currentActions });
                    }
                }
            };
        }
        return node;
    }), [nodes, updateNodeData]);

    const onNodesChange = useCallback((changes: NodeChange[]) => setNodes((nds) => applyNodeChanges(changes, nds)), []);
    const onEdgesChange = useCallback((changes: EdgeChange[]) => setEdges((eds) => applyEdgeChanges(changes, eds)), []);
    const onConnect = useCallback((connection: Connection) => setEdges((eds) => addEdge(connection, eds)), []);

    const onDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const type = e.dataTransfer.getData(DRAG_TYPE) as 'triggerNode' | 'stepNode' | 'actionNode';
        if (!type) return;
        const instance = reactFlowRef.current;
        const position = instance?.screenToFlowPosition?.({ x: e.clientX, y: e.clientY }) ?? { x: 280, y: 200 };
        const id = type === 'triggerNode' ? `trigger-${Date.now()}` : type === 'stepNode' ? `step-${Date.now()}` : `action-${Date.now()}`;
        const newNode: Node = {
            id,
            type,
            position,
            data: type === 'triggerNode' ? { keywords: [] } : type === 'stepNode' ? { question: '', variable: '' } : { actions: [], completion_message_success: '', completion_message_failure: '' }
        };
        setNodes((nds) => [...nds, newNode]);
    }, []);

    const handleSave = useCallback(() => {
        if (!flowName.trim()) return alert("Please enter a flow name.");

        const triggerNodes = nodes.filter(n => n.type === 'triggerNode');
        const triggerKeywords = (triggerNodes[0]?.data?.keywords || []) as string[];

        const actionNodes = nodes.filter(n => n.type === 'actionNode');
        const actionData = actionNodes[0]?.data || {};
        const actions = (actionData.actions || []) as FlowAction[];
        const completionSuccess = (actionData.completion_message_success ?? '') as string;
        const completionFailure = (actionData.completion_message_failure ?? '') as string;

        const steps: FlowStep[] = [];
        const startTrigger = triggerNodes[0];
        if (!startTrigger) {
            onSave({
                ...flowData,
                name: flowName,
                trigger_keywords: [],
                steps: [],
                actions,
                completion_message_success: completionSuccess,
                completion_message_failure: completionFailure
            });
            return;
        }
        let currentNodeId = startTrigger.id;
        for (let i = 0; i < 100; i++) {
            const edge = edges.find(e => e.source === currentNodeId);
            if (!edge) break;
            const target = nodes.find(n => n.id === edge.target);
            if (target?.type === 'stepNode') {
                steps.push({
                    id: target.id,
                    type: 'text',
                    question: (target.data?.question as string) || '',
                    variable: (target.data?.variable as string) || ''
                });
                currentNodeId = target.id;
            } else if (target?.type === 'actionNode') {
                break;
            } else {
                break;
            }
        }

        onSave({
            ...flowData,
            name: flowName.trim(),
            trigger_keywords: triggerKeywords,
            steps,
            actions,
            completion_message_success: completionSuccess,
            completion_message_failure: completionFailure
        });
    }, [flowName, nodes, edges, flowData, onSave]);

    return (
        <div className="flow-diagram-editor d-flex flex-column h-100" style={{ minHeight: '600px', background: '#f1f5f9' }}>
            <div className="flow-editor-toolbar bg-white border-bottom px-3 py-2 d-flex align-items-center shadow-sm">
                <Button variant="light" size="sm" className="rounded-circle p-2" onClick={onCancel} title="Back to list">
                    <FaArrowLeft />
                </Button>
            </div>

            <div className="flow-editor-body d-flex" style={{ minHeight: 0, height: 'calc(70vh - 52px)' }}>
                <aside
                    className="flow-toolbox-sidebar bg-white border-end d-flex flex-column py-3 px-3"
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                >
                    <div className="flow-sidebar-name mb-3">
                        <label className="form-label small text-muted text-uppercase fw-bold mb-1">Flow name</label>
                        <Form.Control
                            type="text"
                            placeholder="e.g. Lead capture"
                            value={flowName}
                            onChange={e => setFlowName(e.target.value)}
                            className="flow-name-input"
                        />
                    </div>
                    <div className="flow-toolbox flex-grow-1">
                        <div className="fw-bold small text-muted text-uppercase mb-2">Components</div>
                        <p className="small text-muted mb-3">Drag onto canvas to add</p>

                        <div
                            className="flow-toolbox-item flow-toolbox-trigger"
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, 'triggerNode'); e.dataTransfer.effectAllowed = 'move'; }}
                        >
                            <span className="flow-toolbox-icon trigger"><FaBolt /></span>
                            <span className="fw-semibold">Trigger</span>
                            <span className="small text-muted d-block">Starts flow when user says keywords</span>
                        </div>
                        <div
                            className="flow-toolbox-item flow-toolbox-step"
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, 'stepNode'); e.dataTransfer.effectAllowed = 'move'; }}
                        >
                            <span className="flow-toolbox-icon step"><FaQuestionCircle /></span>
                            <span className="fw-semibold">Step</span>
                            <span className="small text-muted d-block">Question / collect answer</span>
                        </div>
                        <div
                            className="flow-toolbox-item flow-toolbox-action"
                            draggable
                            onDragStart={(e) => { e.dataTransfer.setData(DRAG_TYPE, 'actionNode'); e.dataTransfer.effectAllowed = 'move'; }}
                        >
                            <span className="flow-toolbox-icon action"><FaCheckCircle /></span>
                            <span className="fw-semibold">Action & Completion</span>
                            <span className="small text-muted d-block">End actions + message</span>
                        </div>
                    </div>
                    <div className="flow-sidebar-actions pt-3 mt-auto">
                        <div className="d-flex gap-2 flex-nowrap">
                            <Button onClick={onCancel} className="flow-sidebar-btn chatbots-actions-cancel">
                                Cancel
                            </Button>
                            <Button onClick={handleSave} className="flow-sidebar-btn chatbots-actions-save">
                                <Icon icon={FaSave} className="me-1" /> Save
                            </Button>
                        </div>
                    </div>
                </aside>
                <div
                    className="flow-editor-canvas-wrapper flex-grow-1 position-relative"
                    style={{ minHeight: 0 }}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                >
                    <ReactFlow
                        ref={reactFlowRef}
                        nodes={nodesWithHandlers}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={nodeTypes}
                        fitView
                        fitViewOptions={{ padding: 0.2 }}
                        attributionPosition="bottom-right"
                    >
                        <Background color="#cbd5e1" gap={16} />
                        <Controls />
                    </ReactFlow>
                </div>
            </div>
        </div>
    );
};

export default FlowDiagramEditor;
